import Vimeo from '@vimeo/player';

export default class Video {
  // Source: https://unicorntears.dev/posts/how-to-implement-a-seamless-responsive-video-background-using-youtube-and-wordpress/
  constructor() {
    this.videos = document.querySelectorAll('.js-video');
    if (!this.videos || this.videos.length === 0) {
      return;
    }

    this.addScript();
    this.render();
    this.updateViewport();
  }

  /**
   * Append YouTube Asset script (only once)
   */
  addScript() {
    let videoTypes = [];

    [...this.videos].forEach((video) => {
      videoTypes.push(video.dataset.videoType);
    });

    if (videoTypes.indexOf('youtube') !== -1) {
      const tag = document.createElement('script');
      tag.src = "https://www.youtube.com/iframe_api";

      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  }

  /**
   * Render video
   */
  render() {
    window.onload = () => {
      this.getVideo((video) => {
        if (!video.dataset.player || !video.dataset.video) {
          return;
        }
        if (video.dataset.videoType === 'vimeo') {
          this.vimeo(video);
        } else {
          this.onYouTubeIframeAPIReady(video.dataset.player, video.dataset.video);
        }
        this.setDimensions(video);
      });
    };
  }

  async vimeo(video) {
    const iframe = video.querySelector('iframe')
    const options = {
      controls: false,
      title: false
    };
    const player = new Vimeo(iframe, options);
    const duration = await player.getDuration();
    const playButton = document.querySelector(`.js-toggle-player[data-target="${video.dataset.player}"]`);
    const durationText = document.querySelector(`.js-video-duration[data-target="${video.dataset.player}"]`);
    const playButtonIcon = playButton.querySelector('svg');
    const iconPlayClass = 'icon-play';
    const iconPauseClass = 'icon-pause';
    let isPlaying = false;

    playButton.addEventListener('click', () => {
      if (!isPlaying) {
        player.play();
        window.dataLayer.push({
          'event': 'Vimeo video',
          'status': 'start'
        });
      } else {
        player.pause();
        window.dataLayer.push({
          'event': 'Vimeo video',
          'status': 'pauze'
        });
      }
    });

    durationText.innerText = this.formatTime(duration);

    video.classList.add('video--loaded');
    // Accessibility; otherwise shows focus
    video.setAttribute('tabindex', -1);

    player.on('play', function () {
      isPlaying = true
      video.classList.add('video--playing');
      playButtonIcon.classList.add(iconPauseClass);
      playButtonIcon.classList.remove(iconPlayClass);

      video.classList.remove('video--paused');
    });

    // Video Ended
    player.on('ended', function () {
      window.dataLayer.push({
        'event': 'Vimeo video',
        'status': 'ended'
      });

      isPlaying = false;
      video.classList.remove('video--playing');
      playButtonIcon.classList.remove(iconPauseClass);
      playButtonIcon.classList.add(iconPlayClass);
      video.classList.add('video--paused');
    });
  }

  /**
   * Render YouTube Video
   * @param id
   * @param video
   */
  onYouTubeIframeAPIReady(id, video) {
    const playButton = document.querySelector(`.js-toggle-player[data-target="${id}"]`),
      playButtonIcon = playButton.querySelector('svg'),
      iconPlayClass = 'icon-play',
      iconPauseClass = 'icon-pause',
      language = document.documentElement.lang;

    const player = new YT.Player(id, {
      videoId: video,
      playerVars: {
        'autoplay': 0,
        'modestbranding': 1,
        'playsinline': 1,
        'showinfo': 0,
        'rel': 0,
        'controls': 0,
        'color': 'white',
        'loop': 0,
        'mute': 0,
        'playlist': video,
        'hl': language,
        'cc': 1,
        'cc_lang_pref': language
      },
      events: {
        'onReady': () => {
          // Set video-duration
          document.querySelector('.js-video-duration[data-target="' + id + '"]')
            .innerText = this.formatTime(player.getDuration());

          // Set video loaded class
          player.getIframe().parentNode.classList.add('video--loaded');

          // Accessibility; otherwise shows focus
          player.getIframe().setAttribute('tabindex', -1);
        },
        'onStateChange': (playerStatus) => {
          if (playerStatus.data === 0) {
            // Ended
            player.getIframe().parentNode.classList.remove('video--playing');

            window.dataLayer.push({
              'event': 'YouTube video',
              'status': 'end'
            });
          } else if (playerStatus.data === 1) {
            // Playing
            player.getIframe().parentNode.classList.add('video--playing');
            playButtonIcon.classList.add(iconPauseClass);
            playButtonIcon.classList.remove(iconPlayClass);

            player.getIframe().parentNode.classList.remove('video--paused');

            window.dataLayer.push({
              'event': 'YouTube video',
              'status': 'start'
            });
          } else if (playerStatus.data === 2) {
            // Paused
            player.getIframe().parentNode.classList.remove('video--playing');
            player.getIframe().parentNode.classList.add('video--paused');

            playButtonIcon.classList.add(iconPlayClass);
            playButtonIcon.classList.remove(iconPauseClass);

            window.dataLayer.push({
              'event': 'YouTube video',
              'status': 'pauze'
            });
          }
        }
      }
    });

    // Toggle play/pause button
    playButton.addEventListener('click', () => {
      if (player.playerInfo.playerState !== 1) {
        player.playVideo();
      } else if (player.playerInfo.playerState === 1) {
        player.pauseVideo();
      }
    });
  }

  getVideo(callback) {
    [...this.videos].forEach((video) => {
      callback(video)
    })
  }

  /**
   * Format the time to a readable one.
   * @param time
   * @source https://tutorialzine.com/2015/08/how-to-control-youtubes-video-player-with-javascript
   * @return {string}
   */
  formatTime(time) {
    time = Math.round(time);

    const minutes = Math.floor(time / 60),
      seconds = (time - minutes * 60).toString().padStart(2, '0');

    return minutes + ":" + seconds;
  }

  /**
   * Set video dimension, normaly 16 by 9, now 4 by 5.
   * @param video
   */
  setDimensions(video) {
    // force 4:5 ratio
    let videoHeight = ((video.offsetWidth / 4) * 5);

    const spareHeight = window.innerHeight - video.getBoundingClientRect().top;

    if (spareHeight <= videoHeight) {

      const ratio = (spareHeight - 20) / videoHeight;

      // Reduce based on the ratio
      videoHeight = videoHeight * ratio;

      // Only set variable when screenheight is to small, fallback is done with CSS
      video.style.setProperty('--video-width', (video.offsetWidth * ratio) + "px");
    }

    video.style.setProperty('--video-height', videoHeight + "px");
  }

  /**
   * Update video dimensions
   */
  updateViewport() {
    console.log('update view')
    window.addEventListener('orientationchange', () => {
      this.getVideo((video) => {
        this.setDimensions(video);
      })
    });
    window.addEventListener('resize', () => {
      this.getVideo((video) => {
        this.setDimensions(video);
      })
    });
  }

  /**
   * Update image holder
   * @param el
   */
  updateImageViewport(el) {
    window.addEventListener('orientationchange', () => {
      this.setDimensions(el);
    });
    window.addEventListener('resize', () => {
      this.setDimensions(el);
    });
  }
}
